import { faCaretDown, faCaretUp, faKeyboard, faXmark } from "@fortawesome/free-solid-svg-icons";
import CalendarRange, { CalendarView } from "components/common/calendar/CalendarRange";
import If from "components/common/If";
import { FormContextManager, getFormContext } from "config/FormContext";
import React, { createRef, useContext } from "react";
import { InputErrorType } from "types/form-types";
import { dateToString } from "utilities/calendar-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import { validateInput } from "utilities/input-utilities";
import { BaseInputPropsType } from "../Input";
import InputErrors from "../input-common/InputErrors";
import InputExtension from "../input-common/InputExtension";
import InputLabel from "../input-common/InputLabel";
import * as lodash from 'lodash';
import "./DateInput.scss";

type DateRangeInputPropsType = Omit<BaseInputPropsType<HTMLInputElement, Date[]>, 'onChange'> & {
    onChange?: (s: Date[] | undefined, event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | React.ChangeEvent | undefined) => void
    onBlur?: (s: Date[] | undefined, event: React.MouseEvent | React.KeyboardEvent) => void,
    min?: Date
    max?: Date,
    disabledDates?: Date[],
    calculateAsNights?: boolean,
    selectOnly?: 'start-end' | 'all',
    format?: DateFormats,
    view?: CalendarView
}

type DateRangeInputPropsClassType = DateRangeInputPropsType & {
    formContext: FormContextManager
}

type DateRangeInputStateType = {
    touched: boolean
    errors: InputErrorType[],
    inputValue: string,
    dates: Date[],
    opened: boolean,
    loading: boolean,
    isFocused: boolean,
    keyboardActive: boolean,
}

class DateRangeInputClass extends React.Component<DateRangeInputPropsClassType, DateRangeInputStateType> {
    private readonly input = createRef<HTMLInputElement>();
    private readonly inputWrapper = createRef<HTMLDivElement>();
    private readonly dropdownWrapper = createRef<HTMLDivElement>();
    private readonly popoverTargetName;
    constructor(props: DateRangeInputPropsClassType) {
        super(props);
        this.state = {
            touched: false,
            errors: [],
            inputValue: '',
            dates: [],
            opened: false,
            loading: false,
            isFocused: false,
            keyboardActive: false,
        }
        this.popoverTargetName = `${this.props.name}-date-range-input-calendar-popover-target`;
    }

    componentDidMount(): void {
        document.addEventListener('click', this.onFocusOut);
        this.validate(!this.state.touched);
    }

    componentDidUpdate(prevProps: Readonly<DateRangeInputPropsClassType>): void {
        if (prevProps.value != this.props.value || this.props?.value?.length != prevProps.value?.length ||
            !this.containsAll(this.props.value!, prevProps.value!)) {
            let value = this.props.value;
            // if(this.props.value?.length && this.props.value?.length > 1) {
            //     value = this.addMissingDates(this.props.value);
            //     this.onChange(value || [], undefined)
            // }
            this.setState({
                touched: true,
                inputValue: this.format(value) || ''
            })
            this.validate(true);
        }
    }

    // all element in array a contains at least one element form array b
    private containsAll(a: Date[], b: Date[]) {
        return b.every(x => a.some(y => y.getTime() === x.getTime()));
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.onFocusOut);
    }

    private onInputValueChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.disabled || this.props.readonly) return;
        let val = event.target.value;
        let selectStartIndex = 0;

        if (val && (event.nativeEvent as InputEvent).inputType !== 'deleteContentBackward') {
            const toDayDate = this.props.value?.[0] || new Date();
            selectStartIndex = val.length + 1;
            if (/^[0-9]{2}$/.test(val)) {
                val += `/${String(toDayDate.getMonth() + 1).padStart(2, '0')}/${toDayDate.getFullYear()} - `;
            } else if (/^[0-9]{2}\/[0-9]{2}$/.test(val)) {
                val += `/${toDayDate.getFullYear()} - `;
            } else if (/^[0-9]{2}[/][0-9]{2}[/][0-9]{4}[ ]-[ ][0-9]{2}$/.test(val)) {
                val += `/${String(toDayDate.getMonth() + 1).padStart(2, '0')}/${toDayDate.getFullYear()}`;
            } else if (/^[0-9]{2}[/][0-9]{2}[/][0-9]{4}[ ]-[ ][0-9]{2}[/][0-9]{2}$/.test(val)) {
                val += `/${toDayDate.getFullYear()}`;
            }
        }

        this.setState({
            inputValue: val
        });

        if (selectStartIndex == 0) {
            const splitVal = val.split(' - ');
            if (splitVal.length == 1 && DateUtil.isValid(splitVal[0])) {
                this.onChange([DateUtil.ToDate(splitVal[0], DateFormats.LONG)!], event);
            } else if (splitVal.length == 2 && DateUtil.isValid(splitVal[0]) && DateUtil.isValid(splitVal[1])) {
                const startDate = DateUtil.ToDate(splitVal[0], DateFormats.LONG)!;
                const endDate = DateUtil.ToDate(splitVal[1], DateFormats.LONG)!;
                if (startDate.getTime() <= endDate.getTime()) {
                    this.onChange([startDate, endDate], event);
                }
            }
        }

        if (selectStartIndex > 0) {
            setTimeout(() => {
                if (this.input.current) {
                    this.input.current.setSelectionRange(selectStartIndex, val.length);
                }
            }, 100);
        }
    }

    private onInputFocus = async (event: React.FocusEvent<HTMLInputElement>) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false,
                isFocused: true
            });
            return;
        }

        event.target.select();
        this.setOpened(true, { touched: true });
    }

    private onFocusOut = (e: MouseEvent | KeyboardEvent | FocusEvent) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false
            });
            return;
        }
        if (this.inputWrapper.current && this.inputWrapper.current.contains(e.target as Node)) {
            return;
        }

        this.setOpened(false, { inputValue: this.format(this.props.value) });
        this.validate(!this.state.touched);
    }

    private format(date: Date[] | undefined) {
        if (!date || !date.length) return '';
        let dates = lodash.orderBy(date).map(d => DateUtil.format(d, this.props.format));
        return dates.length === 1 ? `${dates[0]} - ` : `${dates[0]} - ${dates[dates.length - 1]}`;
    }

    private setOpened = (open: boolean, state?: Partial<DateRangeInputStateType>) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false
            });
            return;
        }
        this.setState({
            ...this.state,
            opened: open,
            ...state
        });

        if (open) {
            if (!this.dropdownWrapper.current?.matches(':popover-open')) {
                this.dropdownWrapper.current?.showPopover();
            }
        }
        else {
            if (this.dropdownWrapper.current?.matches(':popover-open')) {
                this.dropdownWrapper.current?.hidePopover();
            }
        }

        if (!open && this.state.touched) this.validate(this.state.touched);
    }

    private onKeyPress = (e: React.KeyboardEvent) => {
        if (this.props.disabled || this.props.readonly) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.setOpened(false);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            this.setOpened(!this.state.opened);
        } else if (e.key === 'Tab') {
            this.setOpened(false);
        } else if (e.key === 'ArrowDown') {
            this.setOpened(true);
        } else if (e.key === 'ArrowUp') {
            this.setOpened(true);
        }
    }

    private onChange = (i: Date[] | undefined, event: React.MouseEvent | React.KeyboardEvent | React.ChangeEvent | undefined) => {
        if (typeof this.props.onChange == 'function') {
            this.props.onChange(i, event);
        }

        this.setOpened(false, {
            dates: i
        });
    };

    private validate = (soft?: boolean) => {
        if (this.state.touched) soft = false;
        const customErrors = [...(this.props.customErrors || [])];
        if (this.props.required) {
            customErrors.push({
                name: 'valueMissing',
                message: 'form.errors.valueMissing',
                validate: i => {
                    return this.props.value && (this.props.value.length === 0 || this.props.value.length > 1) ? true : false;
                },
            })
        }

        if (this.props.min) {
            customErrors.push({
                name: 'rangeUnderflow',
                message: 'form.errors.rangeUnderflow',
                validate: i => {
                    if (!this.props.value) return true;
                    return this.props.value.some(x => dateToString(x) < dateToString(this.props.min!)) ? false : true;
                },
            })
        }

        if (this.props.max) {
            customErrors.push({
                name: 'rangeOverflow',
                message: 'form.errors.rangeOverflow',
                validate: i => {
                    if (!this.props.value) return true;
                    return this.props.value.some(x => dateToString(x) > dateToString(this.props.max!)) ? false : true;
                },
            })
        }

        if (this.props.disabledDates?.length) {
            customErrors.push({
                name: 'disabledDates',
                message: 'form.errors.disabledDates',
                validate: i => {
                    if (!this.props.value) return true;
                    if (this.props.value.length < 2) return true;

                    return this.props.disabledDates?.some(d => {
                        return this.props.value?.some(x => dateToString(x) === dateToString(d))
                    }) ? false : true;
                },
            })
        }

        customErrors.push({
            name: 'invalidFormat',
            message: 'form.errors.patternMismatch',
            validate: i => {
                if (!this.props.value) return true;
                return this.props.value.some(x => !DateUtil.isValid(x)) ? false : true;
            },
        })

        const errors = validateInput({
            value: this.props.value,
            input: this.input.current!,
            inputWrapper: this.inputWrapper.current!,
            customErrors: customErrors,
            soft: soft ? true : false
        });

        this.setState({
            errors: errors || []
        })
    }

    private onClear = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.onChange([], e);
    }

    render() {
        return (
            <div
                ref={this.inputWrapper}
                className={`popup-anchor-calendar-range app-input-wrapper dropdown-input relative box-border ${this.props.disabled ? 'disabled' : ''} ${this.props.readonly ? 'readonly' : ''}`.trim()}
                popoverTarget={this.popoverTargetName}
            >
                <label>
                    <InputLabel label={this.props.label} required={this.props.required} />
                    <div
                        className="input-container flex"
                    >
                        <InputExtension isFocused={this.state.isFocused} icons={this.props.prefix} />
                        <input
                            ref={this.input}
                            type="text"
                            name={this.props.name}
                            autoFocus={this.props.autoFocus}
                            size={50}
                            onFocus={this.onInputFocus}
                            className={`${this.props.prefix ? '' : 'pl'} ${this.props.suffix ? '' : 'pr'}`.trim()}
                            onKeyUp={this.onKeyPress}
                            value={this.state.inputValue}
                            inputMode={this.state.keyboardActive ? undefined : 'none'}
                            onBlur={() => this.setState({ isFocused: false })}
                            onChange={this.onInputValueChange}
                            placeholder={`${this.props.placeholder || ''}${this.props.required ? ' *' : ''}`}
                        />
                        <InputExtension key='dropdown-clear' icons={{ icon: faXmark, name: 'clear', hidden: () => !this.props.required && this.props.value ? false : true, onClick: this.onClear }} />
                        <InputExtension icons={this.props.suffix} isFocused={this.state.isFocused} />
                        <InputExtension key='dropdown-open' icons={{
                            icon: faCaretDown,
                            name: `${this.props.name}-dateinputrange-open`,
                            title: 'button.open',
                            hidden: () => this.state.opened,
                            onClick: (e) => this.setOpened(true)
                        }}
                        />
                        <InputExtension key='dropdown-close' icons={{
                            icon: faCaretUp,
                            name: `${this.props.name}-dateinputrange-close`,
                            title: 'button.close',
                            hidden: () => !this.state.opened,
                            onClick: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                this.setOpened(false)
                            }
                        }} />
                    </div>
                </label>
                <If condition={this.props.description}>
                    <div className="text-xs">
                        {this.props.description}
                    </div>
                </If>
                <InputErrors errors={this.state.errors} />
                <div popover="manual" className="popup-anchor-calendar-range-dropdown min-w-[350px] border border-color shadow-xl" ref={this.dropdownWrapper} id={this.popoverTargetName}>
                    <CalendarRange
                        name={this.props.name || ''}
                        onDatesSelected={this.onChange}
                        selectedDates={this.props.value}
                        maxDate={this.props.max}
                        minDate={this.props.min}
                        disabledDates={this.props.disabledDates}
                        calculateAsNights={this.props.calculateAsNights}
                        view={this.props.view}
                    />
                </div>
            </div>
        );
    }
}

const DateRangeInput = (props: DateRangeInputPropsType) => {
    const formContext = getFormContext(useContext);
    return (
        <DateRangeInputClass
            {...props}
            formContext={formContext}
        />
    );
};

export default DateRangeInput;
